import cx from 'classnames';
import React from 'react';
import { render } from 'react-dom';
import { Link as v4Link, withRouter, Redirect } from 'react-router-dom';
import promise from 'promise';
import promiseFinally from 'promise.prototype.finally';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { StickyContainer } from 'react-sticky';
import throttle from 'lodash/throttle';
import Cookies from 'js-cookie';

import Routes from 'app/shared/routes';
import {
    Footer,
    ResponsiveHeading,
} from '@zillow/zrs-comet';

import {
    Banner,
    CloseButton,
} from '@zillow/constellation';

import HeaderNavSOH from '@zg-rentals/zrm-utils-js/lib/HeaderNavSOH';
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';
import '@zillow/reg-login/src/less/reg-login.less';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// service
import appLogger from 'app/shared/loggers/appLogger';

// instant app
import instantAppUtils from 'app/client/pwa/instantapp';

// service worker
import SW_CONFIG from 'app/client/workers/config';
import { COOKIE, SW_ACTION } from 'app/client/workers/constants';
import swMessageBus from 'app/client/workers/swMessageBus';
import cacheStorageUtils from 'app/client/workers/utils/cacheStorage';

// notification
import NotificationModule from 'app/client/workers/notificationModule';

// utils
import analyticsUtils from 'app/client/utils/analyticsUtils';
import stringHelper from 'app/shared/utils/stringHelper';

import { customDimensionsUtils } from '@zg-rentals/particles-js-analytics';
import Constants from 'app/shared/constants';
import StringConstants from 'app/shared/constants/StringConstants';
import AppActions from 'app/shared/actions/AppActions';
import ModalActions from 'app/shared/actions/ModalActions';
import UserActions from 'app/shared/actions/UserActions';
import browserUtils from 'app/shared/utils/browserUtils';
import userUtils from 'app/shared/utils/userUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import Receivers from 'app/client/workers/receivers';

import {
    AppSelectors,
    RoutingSelectors,
    UserSelectors,
    PropertiesSelectors,
} from 'app/shared/selectors';
import EnvSelectors from 'app/shared/selectors/EnvSelectors';

// modal
import ModalContainer from 'app/shared/containers/ModalContainer';

// constants
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';

// login modal
import loadable from '@loadable/component';

const RegLogin = loadable(() => import('@zillow/reg-login'));

import labster from 'app/shared/utils/labster';

import { StyledContainer, StyledFooterContainer, GlobalStyle } from './StyledComponents';

// Stylesheets
import './style.scss';

const { startCaseRemoveSpace } = stringHelper;
const TEST = process.env.NODE_ENV === 'test';

if (__CLIENT__ && !TEST) {
    // eslint-disable-next-line global-require
    const FastClick = require('fastclick');

    FastClick.attach(document.body);
}

class App extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        paths: PropTypes.object.isRequired,
        isInMobileWebView: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        this.clearStorage = this.clearStorage.bind(this);
        this.displayBlockedUserModal = this.displayBlockedUserModal.bind(this);
        this.getBrandClassName = this.getBrandClassName.bind(this);
        this.handleNotificationCloseClick = this.handleNotificationCloseClick.bind(this);
        this.handleLogoutClick = this.handleLogoutClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleOffline = this.handleOffline.bind(this);
        this.handleOnline = this.handleOnline.bind(this);
        this.handleStopImpersonatingClick = this.handleStopImpersonatingClick.bind(this);
        this.trackHeaderClick = this.trackHeaderClick.bind(this);
        this.handleFooterClick = this.handleFooterClick.bind(this);
        this.updateAppSWVersion = this.updateAppSWVersion.bind(this);
        this.unregisterSW = this.unregisterSW.bind(this);
        this.setListingIdFromUrl = this.setListingIdFromUrl.bind(this);

        // ab experiments
        this.setExperimentDimensions = this.setExperimentDimensions.bind(this);

        this.state = {
            displayOfflineNotification: false,
            listingId: undefined,
        };
    }

    componentWillMount() {
        this.checkSalesReferral();

        if (__CLIENT__) {
            const { dispatch, user } = this.props;

            // update sw cache storage
            cacheStorageUtils.clearStaleUserSessions(user.userToken);

            // hook up redux’s dispatch to sw receivers
            Receivers(dispatch);
        }
    }

    componentDidMount() {
        const {
            dispatch,
            user,
        } = this.props;

        this.handleResize();

        // register listeners
        window.addEventListener('resize', this.handleThrottledResize);
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);

        // service worker
        if (SW_CONFIG.CLEAR_CACHE_STORAGE) {
            this.unregisterSW().then(() => {
                this.clearStorage();
            });
        } else {
            this.registerSW();
        }

        // instant apps
        instantAppUtils.registerBeforeInstallListener();

        // window might not be available
        try {
            dispatch(AppActions.updateApp({
                isOnline: window.navigator.onLine,
            }));
        } catch (error) {
            appLogger.logError({
                message: error.message,
                context: 'App.componentDidMount',
            });
        }

        // polyfill promise.finally
        promiseFinally.shim();

        if (user.userToken) {
            analyticsUtils.setCustomDimension(
                user.userToken,
                AnalyticsConstants.GTM_CUSTOM_DIMENSION.USER_TOKEN,
                StringConstants.GA_TRACKERS.gtm,
            );

            analyticsUtils.setCustomDimension(
                user.userToken,
                AnalyticsConstants.GTM_CUSTOM_DIMENSION.USER_TOKEN,
                StringConstants.GA_TRACKERS.dsp,
            );
        }

        if (user.features && user.features.hasOwnProperty('canAccessConnectedExperience')) {
            analyticsUtils.setCustomDimension(
                user.features.canAccessConnectedExperience.toString(),
                AnalyticsConstants.GTM_CUSTOM_DIMENSION.IS_SOH,
                StringConstants.GA_TRACKERS.gtm,
            );

            analyticsUtils.setCustomDimension(
                user.features.canAccessConnectedExperience.toString(),
                AnalyticsConstants.GTM_CUSTOM_DIMENSION.IS_SOH,
                StringConstants.GA_TRACKERS.dsp,
            );
        }

        analyticsUtils.setMobileAppCustomDimension();

        // set full referring url
        if (window.document.referrer) {
            analyticsUtils.setCustomDimension(
                window.document.referrer,
                AnalyticsConstants.Z_CUSTOM_DIMENSION.FULL_REFERRING_URL,
                StringConstants.GA_TRACKERS.zillow,
            );
        }

        this.mountRegLogin();
        this.setPageCategoryCustomDimension();
        this.onRouteChange();
    }

    componentWillReceiveProps(nextProps) {
        const { isOnline, user } = this.props;

        // check if user logging in / out
        if (nextProps.isLoggedIn !== this.props.isLoggedIn) {
            if (!nextProps.isLoggedIn) {
                // logged out
                // clear sw
                this.unregisterSW()
                    .then(this.clearStorage);
            }
        } else if (nextProps.user.userToken !== user.userToken) {
            // clear and register sw
            this.unregisterSW()
                .then(this.clearStorage)
                .then(this.registerSW(nextProps.user.userToken));
        }

        if (nextProps.isOnline !== isOnline) {
            this.setState({ displayOfflineNotification: !nextProps.isOnline });
        }
    }

    componentDidUpdate(prevProps) {
        const {
            location,
            dispatch,
            currentProperty,
            listingId,
        } = this.props;

        if (location.pathname !== prevProps.location.pathname) {
            // update prev path
            dispatch(
                AppActions.updateApp({
                    prevPath: prevProps.location.pathname,
                }),
            );

            this.onRouteChange();
        }

        // current property change
        // and has a path with a listingId
        if (currentProperty.listingAlias !== prevProps.currentProperty.listingAlias
            && listingId) {
            this.updateListingCustomDimensions();
        }
    }

    componentWillUnmount() {
        // remove listeners
        window.removeEventListener('resize', this.handleThrottledResize);
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
    }

    setExperimentDimensions() {
        const { dispatch, brand } = this.props;

        dispatch(labster.setExperimentDimensions((experiment) => {
            analyticsUtils.setLabsterExperimentDimension({
                experiment,
                brand,
            });

            analyticsUtils.setLabsterExperimentDimension({
                experiment,
                brand: StringConstants.GA_TRACKERS.dsp,
            });
        }));
    }

    mountRegLogin() {
        const loginAnchor = document.getElementById('reg-login');
        if (loginAnchor) {
            render(
                <RegLogin
                    onSuccess={this.onRegLoginSuccess}
                    isLightbox="true"
                    signInWithAppleEnabled={true}
                />, loginAnchor
            );
        }
    }

    onRegLoginSuccess = () => {
        const { pathname, shouldReloadOnLoginSuccess } = this.props;
        const { paths } = this.context;

        if (!shouldReloadOnLoginSuccess && pathname === `${paths.postAListingPage}-v2/`) {
            return;
        }
        window.location.reload(false);
    }

    setPageCategoryCustomDimension() {
        analyticsUtils.setCustomDimension(
            'rentals',
            AnalyticsConstants.Z_CUSTOM_DIMENSION.PAGE_CATEGORY,
            StringConstants.GA_TRACKERS.zillow,
        );
    }

    setListingIdFromUrl() {
        const split = window.location.href.split('rental-manager/properties/');
        if (split.length === 1) {
            this.setState({ listingId: undefined });
            return;
        }
        const listingId = split.pop().split('/')[0];
        this.setState({ listingId });
    }

    checkSalesReferral() {
        const {
            dispatch,
            pathname,
            queryString,
        } = this.props;

        // make object
        const queryParamObj = ZRMUtils.queryUtils.parse(queryUtils.cleanQueryParams(queryString));

        // if query param contains 'pisrc'
        const code = queryParamObj.pisrc;

        if (code) {
            // store referral
            dispatch(UserActions.addReferral({ code }))
                .then(() => {
                    // remove pisrc
                    delete queryParamObj.pisrc;

                    // set full path
                    const tempQueryParams = queryUtils.stringify(queryParamObj);
                    const locationObj = ZRMUtils.queryUtils.parsePathAndQueryParams(`${pathname}${tempQueryParams}`);
                    const { path, queryParams } = locationObj;
                    const fullPath = `${path}${queryParams}`;

                    // transition
                    window.router.transitionTo(fullPath);
                })
                .catch((errorRMWModel) => {
                    appLogger.logError({
                        message: errorRMWModel.message,
                        context: 'App.checkSalesReferral',
                    });
                });
        }
    }

    clearListingCustomDimensions() {
        analyticsUtils.clearCustomDimension(AnalyticsConstants.Z_CUSTOM_DIMENSION.CITY);
        analyticsUtils.clearCustomDimension(AnalyticsConstants.Z_CUSTOM_DIMENSION.LISTING_ID);
        analyticsUtils.clearCustomDimension(AnalyticsConstants.Z_CUSTOM_DIMENSION.STATE);
        analyticsUtils.clearCustomDimension(AnalyticsConstants.Z_CUSTOM_DIMENSION.ZIP);
    }

    clearStorage() {
        // cache storage will come back on app load if true
        Cookies.set('ZG_SW_REGISTERED', false);

        return cacheStorageUtils.clear();
    }

    pushZguidToGA() {
        const { brand, user } = this.props;
        if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow) {
            const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.ZGUID;
            analyticsUtils.setCustomDimension(user.userToken, dimension, brand);
        }
    }

    logUserDataToGA(user) {
        const {
            isMultifamilyCustomer,
            summary,
            userToken,
            roles,
        } = user;
        const { brand } = this.props;
        const { activeListingCount, totalListingCount } = summary;
        const isRocketshipCustomer = userUtils.isRocketshipCustomer(roles);
        const isNucleusCustomer = userUtils.isNucleusCustomer(roles);
        const isSingleFamilyPayPerListingCustomer = userUtils.isSingleFamilyPayPerListingCustomer(roles);
        const userSummary = customDimensionsUtils.getRentalManagerUserSummary(isMultifamilyCustomer, activeListingCount,
            totalListingCount, userToken, isRocketshipCustomer, isNucleusCustomer, isSingleFamilyPayPerListingCustomer);

        if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow) {
            const { USER_SUMMARY: dimension } = AnalyticsConstants.Z_CUSTOM_DIMENSION;
            analyticsUtils.setCustomDimension(userSummary, dimension, brand);
        }

        analyticsUtils.setCustomDimension(
            userSummary,
            AnalyticsConstants.GTM_CUSTOM_DIMENSION.USER_SUMMARY,
            StringConstants.GA_TRACKERS.gtm
        );

        analyticsUtils.setCustomDimension(
            userSummary,
            AnalyticsConstants.GTM_CUSTOM_DIMENSION.USER_SUMMARY,
            StringConstants.GA_TRACKERS.dsp
        );
    }

    handleResize() {
        const { dispatch } = this.props;
        const width = browserUtils.screen.width();
        dispatch(AppActions.screenDimensionChange(width));
    }

    handleOffline() {
        const { dispatch } = this.props;
        dispatch(AppActions.updateApp({
            isOnline: window.navigator.onLine,
        }));
    }

    handleOnline() {
        const { dispatch } = this.props;
        dispatch(AppActions.updateApp({
            isOnline: window.navigator.onLine,
        }));
    }

    handleStopImpersonatingClick() {
        const { paths } = this.context;

        // clear sw
        this.unregisterSW()
            .then(this.clearStorage)
            .then(window.router.updateLocation(paths.stopImpersonatingPage));
    }

    handleThrottledResize = throttle(() => this.handleResize(), 300);

    registerSW(pUserToken = -1) {
        const { dispatch, user } = this.props;

        if ('serviceWorker' in window.navigator) {
            const userToken = pUserToken === -1 ? user.userToken : pUserToken;

            // register SW and set user token
            // call endpoint on node server, return static file via serveStatic middleware
            window.navigator.serviceWorker
                .register('/rental-manager/public/serviceworker-rmw.js', { scope: '/rental-manager' })
                .then((registration) => {
                    appLogger.logInfo({
                        message: `SW registered with scope: ${registration.scope}`,
                        context: 'App.registerSW',
                    });

                    // communicate to sw
                    if (userToken) {
                        swMessageBus.emit('message', {
                            action: SW_ACTION.SET_USER_TOKEN,
                            payload: userToken,
                        });
                    } else {
                        swMessageBus.emit('message', {
                            action: SW_ACTION.CLEAR_ALL,
                        });
                    }

                    // sw is ready
                    navigator.serviceWorker.ready.then((newRegistration) => {
                        try {
                            NotificationModule.handleSWReady(newRegistration, dispatch);
                        } catch (error) {
                            appLogger.logInfo({
                                message: 'handleSWReady is most likely undefined',
                                context: 'App.registerSW.handleSWReady',
                            });
                        }
                    });

                    // update store
                    Cookies.set(COOKIE.ZG_SW_REGISTERED, true);
                    this.updateAppSWVersion(SW_CONFIG.VERSION);
                })
                .catch(err => appLogger.logInfo({
                    message: `Error registering SW: ${err.message}`,
                    context: 'App.registerSW',
                }));
        } else {
            // no feature
            appLogger.logInfo({
                message: 'SW not available to register. Browser does not have window.navigator',
                context: 'App.registerSW',
            });
            Cookies.set(COOKIE.ZG_SW_REGISTERED, false);
            this.updateAppSWVersion(SW_CONFIG.DEFAULT_VERSION);
        }
    }

    // unregister service worker
    unregisterSW() {
        if ('serviceWorker' in window.navigator) {
            return window.navigator.serviceWorker.getRegistrations()
                .then((registrations) => {
                    if (registrations.length > 0) {
                        registrations.forEach(registration => registration.unregister());
                    }
                    // no registrations found
                    return true;
                }).catch(() => {
                    const { user } = this.props;

                    appLogger.logError({
                        message: 'Service Worker unregistration failed',
                        context: 'unregisterSW',
                        user,
                    });

                    // 'Service Worker unregistration failed
                    return false;
                });
        }
        // no service worker found to unregister
        return promise.resolve(true);
    }

    onRouteChange() {
        const {
            dispatch,
            isUserBlocked,
            listingId,
            location,
            user,
        } = this.props;
        const {
            loggedIn,
        } = user;

        // GA Tagging (in both ZRM GA and MUI GA handled through analytics.js)
        // Log user Data
        this.logUserDataToGA(user);

        // push ZUID to GA via custom dimension
        if (loggedIn) {
            this.pushZguidToGA();
        }

        // Update custom dimensions related to A/B tests
        // and push to UA before tracking page view.
        this.setExperimentDimensions();

        // does not have a path with a listingId
        if (!listingId) {
            this.clearListingCustomDimensions();
        }

        if (isUserBlocked) {
            // user is blocked - track page view as properties page
            dispatch(analyticsUtils.trackPageView(location.propertiesPage));

            this.displayBlockedUserModal();
        } else {
            // user is not blocked - track page view
            dispatch(analyticsUtils.trackPageView(location.pathname));
        }

        this.setListingIdFromUrl();
    }

    displayBlockedUserModal() {
        const {
            brand,
            dispatch,
            history,
        } = this.props;
        const { paths } = this.context;

        // redirect via replace history
        history.replace(paths.propertiesPage);

        // use delay in order for modal to display correctly
        setTimeout(() => {
            dispatch(ModalActions.showModal({
                modalType: ModalConstants.BLOCKED_USER_MODAL,
                header: 'Alert',
                brand,
                shouldCloseOnOverlayClick: false,
            }));
        }, 300);
    }

    getBrandClassName() {
        const { brand } = this.props;

        const classObj = {
            App: true,
            hpx: brand === 'hotpads',
            zsg: brand === 'zillow',
            txl: brand === 'trulia',
        };

        return cx(classObj);
    }

    handleNotificationCloseClick() {
        this.setState({ displayOfflineNotification: false });
    }

    handleLogoutClick() {
        const { brand } = this.props;

        const {
            dispatch,
            user,
            hostname,
        } = this.props;

        /* eslint-disable no-else-return */
        if (brand === Constants.UNFORMATTED_BRAND_NAMES.zillow) {
            return this.unregisterSW()
                .then(() => {
                    dispatch(UserActions.updateUserInfo({
                        loggedIn: false,
                    }));

                    this.clearStorage();
                    this.updateAppSWVersion(SW_CONFIG.DEFAULT_VERSION);
                    Cookies.set(COOKIE.ZG_SW_REGISTERED, false);
                });
        } else if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.hotpads) {
            // deletes cookies
            return dispatch(UserActions.logoutUser(hostname))
                .then(() => {
                    dispatch(UserActions.updateUserInfo({
                        loggedIn: false,
                    }));

                    this.unregisterSW()
                        .then(() => {
                            this.clearStorage();
                            this.updateAppSWVersion(SW_CONFIG.DEFAULT_VERSION);
                        });
                })
                .catch((err) => {
                    appLogger.logError({
                        message: err,
                        context: 'handleLogoutClick',
                        user,
                    });
                });
        }

        return true;
    }

    handleLoginClick = () => {
        const { dispatch } = this.props;
        dispatch(ModalActions.updateShouldReloadOnLoginSuccess(true));
    }

    trackHeaderClick(pageClickingTo) {
        const { dispatch } = this.props;
        dispatch(
            analyticsUtils.track.GAEventV2(Constants.GA_EVENTS.NAV_BAR_CLICK, {
                action: startCaseRemoveSpace(pageClickingTo),
                label: startCaseRemoveSpace(pageClickingTo),
            }),
        );
    }

    handleFooterClick(e) {
        this.props.dispatch(
            analyticsUtils.track.GAEventV2(Constants.GA_EVENTS.FOOTER_NAV_CLICK_TO, {
                action: startCaseRemoveSpace(e.target.innerText),
            }),
        );
    }

    isInComet(pathname) {
        // Check the pathname and decide if we are in comet
        if (!pathname) {
            return false;
        }

        return (
            pathname.includes('applications') || pathname.includes('payments') || pathname.includes('landlord-review')
        );
    }

    updateAppSWVersion(value = SW_CONFIG.DEFAULT_VERSION) {
        const { dispatch } = this.props;
        dispatch(AppActions.updateApp({
            swVersion: value,
        }));
    }

    updateListingCustomDimensions() {
        // connected endpoints use aliasEncoded as listing id
        const {
            currentProperty: {
                aliasEncoded,
                city,
                state,
                zip,
            },
        } = this.props;

        // city
        analyticsUtils.setCustomDimension(
            city,
            AnalyticsConstants.Z_CUSTOM_DIMENSION.CITY,
            StringConstants.GA_TRACKERS.zillow,
        );

        // listingId
        analyticsUtils.setCustomDimension(
            aliasEncoded,
            AnalyticsConstants.Z_CUSTOM_DIMENSION.LISTING_ID,
            StringConstants.GA_TRACKERS.zillow,
        );

        // state
        analyticsUtils.setCustomDimension(
            state,
            AnalyticsConstants.Z_CUSTOM_DIMENSION.STATE,
            StringConstants.GA_TRACKERS.zillow,
        );

        // zip
        analyticsUtils.setCustomDimension(
            zip,
            AnalyticsConstants.Z_CUSTOM_DIMENSION.ZIP,
            StringConstants.GA_TRACKERS.zillow,
        );
    }

    render() {
        const { displayOfflineNotification, listingId } = this.state;
        const {
            brand,
            user,
            pathname,
            queryString,
            location,
            isProduction,
            csrfToken,
        } = this.props;

        const { paths, isInMobileWebView } = this.context;

        const {
            impersonatorEmail,
            impersonatorUserToken,
            loggedIn,
            features,
            isRocketshipCustomerOnly,
        } = user;

        const parsedQueryString = queryString ? ZRMUtils.queryUtils.parse(queryString) : {};

        if (parsedQueryString.itc) {
            analyticsUtils.setCustomDimension(parsedQueryString.itc, Constants.Z_CUSTOM_DIMENSION.ITC, brand);
        }

        const pathAndQuery = `${pathname}${queryString}`;

        // If session expired from 30 minutes of inactivity, while impersonating, we need
        // to redirect user to the login page
        // Locally, this will cause an infinite redirect, so only doing this on __PRODUCTION__
        if (isProduction && impersonatorEmail && impersonatorUserToken && !loggedIn) {
            return <Redirect to={paths.signInPage(location.pathname)} />;
        }

        // provide proxy path to headerNav for making notification center requests
        const proxyPath = paths.proxy;

        const displayAnalytics = features.canAccessMainAnalytics;

        const useAltLogo = !isRocketshipCustomerOnly;
        const rentalManagerApiWebSocket = __CLIENT__ ? window.rentalManagerApiWebSocket : undefined;

        return (
            <StickyContainer>
                <StyledContainer
                    id="App"
                    className={cx(this.getBrandClassName(), { web: !isInMobileWebView })}
                >
                    <div className="App-Body-Wrapper">
                        {displayOfflineNotification && (
                            <Banner
                                appearance="warning"
                                body="You are browsing offline. Please check your internet connection."
                                closeButton={<CloseButton />}
                                onClose={this.handleNotificationCloseClick}
                            />
                        )}
                        {!isInMobileWebView && (
                            <HeaderNavSOH
                                altLogo={useAltLogo}
                                brand={brand}
                                userModel={user}
                                useRelativePaths={true}
                                reactRouterLink={v4Link}
                                pathname={pathAndQuery}
                                proxyPath={proxyPath}
                                csrf_token={csrfToken}
                                webSocketInstance={rentalManagerApiWebSocket}
                                displayAnalytics={displayAnalytics}
                                useLoginModal={true}
                                // events
                                onLoginClick={this.handleLoginClick}
                                handleLogoutClick={this.handleLogoutClick}
                                stickyAdminNav={true}
                                listingId={listingId}
                            />
                        )}
                        <Routes {...this.props} />
                    </div>

                    {!isInMobileWebView && brand === 'zillow' && (
                        <StyledFooterContainer>
                            <Footer onLinkClick={this.handleFooterClick} />
                        </StyledFooterContainer>
                    )}

                    {/** modal **/}
                    <ModalContainer />
                    <GlobalStyle />
                    <ResponsiveHeading />
                </StyledContainer>
            </StickyContainer>
        );
    }
}

const mapStateToProps = state => ({
    appConfig: AppSelectors.makeAppConfig(state),
    brand: AppSelectors.makeBrand(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
    csrfToken: AppSelectors.makeCsrfToken(state),
    hostname: AppSelectors.makeHostname(state),
    isLoggedIn: UserSelectors.makeIsLoggedIn(state),
    isOnline: AppSelectors.makeIsOnline(state),
    isProduction: EnvSelectors.makeIsProduction(state),
    isUserBlocked: UserSelectors.makeIsUserBlocked(state),
    listingId: RoutingSelectors.makeListingId(state),
    pathname: RoutingSelectors.makePathname(state),
    queryString: RoutingSelectors.makeSearch(state),
    user: UserSelectors.makeUser(state),
    currentProperty: PropertiesSelectors.makeCurrentProperty(state),
    zgEnv: EnvSelectors.makeZgEnv(state),
    isRunningInProd: state.app.isRunningInProd,
    shouldReloadOnLoginSuccess: state.modal.shouldReloadOnLoginSuccess,
});

export { App };
export default withRouter(connect(mapStateToProps)(App));
